import axios from '@api';

import {
  transformContinuousSumResponses,
  transformDetails,
  transformEssayResponses,
  transformGlobalFilters,
  transformGridResponses,
  transformMaxDiffResponses,
  transformRankResponses,
  transformRawData,
  transformSurveyResponses,
  transformSurveyResponseCounts,
} from './utilities/helpers';

/**
 * export raw data
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportRawData = async ({ surveyId }) => {
  return axios({
    method: 'get',
    params: {
      survey_id: surveyId,
    },
    url: `/v2/api/survey-dashboard/export/rawdata/xlsx/`,
    withCredentials: true,
  });
};

/**
 * export summary tab
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportSummaryData = async ({ surveyId }) => {
  return axios({
    method: 'get',
    params: {
      survey_id: surveyId,
    },
    url: `/v2/api/survey-dashboard/export/summary/xlsx/`,
    withCredentials: true,
  });
};

/**
 * export crosstabs and stattesting xlsx file
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportStatXtabData = async ({ surveyId }) => {
  return axios({
    method: 'get',
    params: {
      survey_id: surveyId,
    },
    url: `/v2/api/survey-dashboard/export/xtab_stat_sig/xlsx/`,
    withCredentials: true,
  });
};

/**
 * export TURF analysis
 * @param {Object} options
 * @param {Object} [options.query]
 * @return {Promise}
 */
export const exportTurfData = async ({ query }) => {
  return axios({
    method: 'get',
    params: query,
    url: '/v2/api/survey-dashboard/export/turf/xlsx/',
    withCredentials: true,
  });
};

/**
 * export maxdiff preference share file
 * @param {Object} options
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const exportMaxDiffPrefShareData = async ({ surveyId }) => {
  return axios({
    method: 'get',
    params: {
      survey_id: surveyId,
    },
    url: `/v2/api/survey-dashboard/export/preference-share/xlsx/`,
    withCredentials: true,
  });
};

/**
 * read continuous sum responses
 * @param {Object} options
 * @param {String} [options.publicToken]
 * @param {Object} [options.query]
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readContinuousSumResponses = async ({ publicToken, query = {}, signal, surveyId }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      ...Object.keys(query).reduce(
        (acc, key) => ({ ...acc, ...(query[key] && { [key]: query[key] }) }),
        {}
      ),
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/survey-responses/cont-sum',
    withCredentials,
  }).then(({ data }) => transformContinuousSumResponses(data));
};

/**
 * read details (response counts)
 * @param {Object} options
 * @param {String} [options.publicToken]
 * @param {Number} [options.signal] AbortController signal
 * @param {Number} options.surveyId Insights survey id
 * @return {Promise}
 */
export const readDetails = async ({ publicToken, signal, surveyId }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    signal,
    url: `/v2/api/survey-dashboard/details/${surveyId}`,
    withCredentials,
  }).then(({ data }) => transformDetails(data));
};

/**
 * read essay responses
 * @param {Object} options
 * @param {String} options.publicToken
 * @param {Object} [options.query]
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readEssayResponses = async ({ publicToken, query = {}, signal, surveyId }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      ...Object.keys(query).reduce(
        (acc, key) => ({ ...acc, ...(query[key] && { [key]: query[key] }) }),
        {}
      ),
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/essay-responses/table',
    withCredentials,
  }).then(({ data }) => transformEssayResponses(data));
};

/**
 * read global filters
 * @param {Object} options
 * @param {String} options.publicToken
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readGlobalFilters = async ({ publicToken, signal, surveyId }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    signal,
    url: `/v2/api/survey-dashboard/global-filters/${surveyId}`,
    withCredentials,
  }).then(({ data }) => transformGlobalFilters(data));
};

/**
 * read grid responses
 * @param {Object} options
 * @param {String} [options.publicToken]
 * @param {Object} [options.query]
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readGridResponses = async ({ publicToken, query = {}, signal, surveyId }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      ...Object.keys(query).reduce(
        (acc, key) => ({ ...acc, ...(query[key] && { [key]: query[key] }) }),
        {}
      ),
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/survey-responses/grid',
    withCredentials,
  }).then(({ data }) => transformGridResponses(data));
};

/**
 * read max diff responses
 * @param {Object} options
 * @param {String} [options.publicToken]
 * @param {Object} [options.query]
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readMaxDiffResponses = async ({ publicToken, query = {}, signal, surveyId }) => {
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      ...Object.keys(query).reduce(
        (acc, key) => ({ ...acc, ...(query[key] && { [key]: query[key] }) }),
        {}
      ),
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/survey-responses/maxdiff',
    withCredentials,
  }).then(({ data }) => transformMaxDiffResponses(data));
};

/**
 * read rank responses
 * @param {Object} options
 * @param {String} options.publicToken
 * @param {Object} [options.query]
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readRankResponses = async ({ publicToken, query = {}, signal, surveyId }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      ...Object.keys(query).reduce(
        (acc, key) => ({ ...acc, ...(query[key] && { [key]: query[key] }) }),
        {}
      ),
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/survey-responses/rank',
    withCredentials,
  }).then(({ data }) => transformRankResponses(data));
};

/**
 * read raw data
 * @param {Object} options
 * @param {String} options.publicToken
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readRawData = async ({ publicToken, signal, surveyId }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/raw-data/table',
    withCredentials,
  }).then(({ data }) => transformRawData(data));
};

/**
 * read survey responses
 * @param {Object} options
 * @param {String} options.publicToken
 * @param {Object} [options.query]
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readSurveyResponses = async ({ publicToken, query = {}, signal, surveyId }) => {
  // Bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      ...Object.keys(query).reduce(
        (acc, key) => ({ ...acc, ...(query[key] && { [key]: query[key] }) }),
        {}
      ),
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/survey-responses/bar-chart',
    withCredentials,
  }).then(({ data }) => transformSurveyResponses(data));
};

/**
 * read survey response counts
 * @param {Object} options
 * @param {String} options.publicToken
 * @param {Object} [options.query]
 * @param {Number} [options.signal] AbortController signal
 * @param {Number|String} options.surveyId
 * @return {Promise}
 */
export const readSurveyResponseCounts = async ({ publicToken, query = {}, signal, surveyId }) => {
  const withCredentials = publicToken ? false : true;

  return axios({
    method: 'get',
    params: {
      ...Object.keys(query).reduce(
        (acc, key) => ({ ...acc, ...(query[key] && { [key]: query[key] }) }),
        {}
      ),
      publicToken,
      survey_id: surveyId,
    },
    signal,
    url: '/v2/api/survey-dashboard/survey-response-counts',
    withCredentials,
  }).then(({ data }) => transformSurveyResponseCounts(data));
};
